import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import cn from 'clsx';
import { useClipboard } from 'use-clipboard-copy';
import styles from './style.module.scss';
import Button from '@components-clean/core/button';
import { AppVersion } from '@interfaces/version';
import { isDevOrTestEnv } from '@utils-clean/environment-helpers';
import { getSessionStorageToken } from '@contexts/local-state-context';
import { useTranslation } from '@hooks/index';
import NotificationService from '@utils/notification';
import Settings from '@settings/settings';
import {
    useGetCentralMonitoringServiceVersion,
    useGetIntegrationsServiceVersion,
    useGetReportServiceVersion,
    useGetWebAPIVersion,
} from '@apis/bff-api/version';

interface EnvIndicatorListProps {
    environment: string | undefined;
}

interface EnvAppVersion extends AppVersion {
    version?: string;
}

export const trimVersion = (versionString: string | null | undefined): string => {
    if (!versionString) return '';

    const branchIndex = versionString.indexOf('Branch') - 1; // Branch is prefixed by . or +
    if (branchIndex === -1) return ''; // string begins with 'Branch', so no version info

    const result = versionString.substring(0, branchIndex >= 0 ? branchIndex : versionString.length);
    return result.replace('+', '.');
};

const InfoItemDisplay = ({ title, info = ' ' }: { title: string; info: string | null | undefined }) => (
    <p className="k-m-0 k-mb-1">
        <h4 className="k-m-0 k-white-space-nowrap">{title}:</h4>
        <span className="k-white-space-nowrap" style={{ minHeight: '17px' }}>
            {trimVersion(info) || '-'}
        </span>
    </p>
);

// TODO: refactor to use ref as a prop - forwardRef was deprecated in React 19
// Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
// eslint-disable-next-line @eslint-react/no-forward-ref
const EnvIndicatorList = forwardRef<HTMLDivElement, EnvIndicatorListProps>(({ environment }, forwardedRef) => {
    const { t } = useTranslation('home');
    const version = import.meta.env.VITE_VERSION as string;
    const [coreApiVersion, setCoreApiVersion] = useState<string | null>(null);
    const [corePodName, setCorePodName] = useState<string>('');
    const [jsReportsApiVersion, setJsReportsApiVersion] = useState<string | null>(null);
    const [powerBiApiVersion, setPowerBiApiVersion] = useState<string | null>(null);
    const [frontendMessageHubVersion, setFrontendMessageHubVersion] = useState<string | null>(null);
    const clipboard = useClipboard();
    const [isCopyToClipBoard, setIsCopyToClipBoard] = useState<boolean>(false);

    const { data: dataWebAPIVersion, isLoading: webAPIVersionLoading } = useGetWebAPIVersion();
    const { data: dataReportServiceVersion, isLoading: reportServiceVersionLoading } = useGetReportServiceVersion();
    const { data: dataCentralMonitoringServiceVersion, isLoading: centralMonitoringServiceVersionLoading } =
        useGetCentralMonitoringServiceVersion();
    const { data: dataIntegrationsServiceVersion, isLoading: integrationsServiceVersionLoading } =
        useGetIntegrationsServiceVersion();

    const fetchApiVersion = async (url: string, setter: (value: string) => void) => {
        const response = await fetch(url);
        const result: EnvAppVersion = (await response.json()) as EnvAppVersion;
        setter((result.productVersion ?? result.version) as string);
    };

    const fetchPodName = async (url: string, setter: (value: string) => void) => {
        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getSessionStorageToken()}`,
            },
        });
        const result = (await response.json()) as { podName: string };
        setter(result.podName);
    };

    const ref = useRef<HTMLDivElement>(null);
    useImperativeHandle(forwardedRef, () => ref.current as HTMLDivElement);

    const copyToClipboard = () => {
        clipboard.copy(ref.current!.innerText);
        NotificationService.showNotification(t('common:copiedToClipboard'));
    };

    useEffect(() => {
        const apiBaseUrl = Settings.api.url();

        fetchApiVersion(`${apiBaseUrl}/api/version`, setCoreApiVersion).catch(() => {
            setCoreApiVersion('');
        });

        fetchApiVersion(`${apiBaseUrl}/api/version/JsReports`, setJsReportsApiVersion).catch(() => {
            setJsReportsApiVersion('');
        });

        fetchApiVersion(`${apiBaseUrl}/powerbi-api/AppVersion`, setPowerBiApiVersion).catch(() => {
            setPowerBiApiVersion('');
        });

        fetchPodName(`${apiBaseUrl}/api/version/podname`, setCorePodName).catch(() => {
            setCorePodName('');
        });

        fetchApiVersion(Settings.api.frontendMessageHubVersionUrl(), setFrontendMessageHubVersion).catch(() => {
            setFrontendMessageHubVersion('');
        });
    }, []);

    useEffect(() => {
        if (
            coreApiVersion != null &&
            jsReportsApiVersion != null &&
            powerBiApiVersion != null &&
            !webAPIVersionLoading &&
            !reportServiceVersionLoading &&
            !centralMonitoringServiceVersionLoading &&
            !integrationsServiceVersionLoading &&
            !(isDevOrTestEnv() && corePodName === null) &&
            frontendMessageHubVersion != null
        ) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setIsCopyToClipBoard(true);
        }
    }, [
        coreApiVersion,
        jsReportsApiVersion,
        powerBiApiVersion,
        webAPIVersionLoading,
        reportServiceVersionLoading,
        centralMonitoringServiceVersionLoading,
        integrationsServiceVersionLoading,
        corePodName,
        frontendMessageHubVersion,
    ]);

    return (
        <div className={cn(styles.infoBlock, 'k-rounded-md')} ref={ref}>
            <InfoItemDisplay title={t('envIndicator.envName')} info={environment} />
            <InfoItemDisplay title={t('envIndicator.webVersion')} info={version} />
            <InfoItemDisplay title={t('envIndicator.opraCoreVersion')} info={coreApiVersion} />
            <InfoItemDisplay title={t('envIndicator.pbiGatewayVersion')} info={powerBiApiVersion} />
            <InfoItemDisplay title={t('envIndicator.jsReportsVersion')} info={jsReportsApiVersion} />
            <InfoItemDisplay title={t('envIndicator.webAPIVersion')} info={dataWebAPIVersion?.productVersion} />
            <InfoItemDisplay title={t('envIndicator.frontendMessageHubVersion')} info={frontendMessageHubVersion} />
            <InfoItemDisplay
                title={t('envIndicator.reportServiceVersion')}
                info={dataReportServiceVersion?.productVersion}
            />
            <InfoItemDisplay
                title={t('envIndicator.centralMonitoringServiceVersion')}
                info={dataCentralMonitoringServiceVersion?.productVersion}
            />
            <InfoItemDisplay
                title={t('envIndicator.integrationsServiceVersion')}
                info={dataIntegrationsServiceVersion?.productVersion}
            />
            {isDevOrTestEnv() && <InfoItemDisplay title={t('envIndicator.opraCorePod')} info={corePodName} />}
            <div className="k-text-right">
                <Button
                    type="button"
                    className={cn('!k-bg-transparent k-border-0 !k-p-1 k-outline-none', styles.button)}
                    onClick={() => copyToClipboard()}
                    data-testid="copy-env-to-clipboard"
                    disabled={!isCopyToClipBoard}
                    faIcon={['far', 'copy']}
                />
            </div>
        </div>
    );
});

export default EnvIndicatorList;
